import React, { useMemo } from "react";

import DataGrid, {
  Column,
  ColumnChooser,
  Grouping,
  GroupItem,
  GroupPanel,
  HeaderFilter,
  Item,
  Paging,
  SearchPanel,
  Selection,
  Summary,
  Toolbar,
  TotalItem,
} from "devextreme-react/data-grid";
import { useTranslation } from "react-i18next";

import { useWindowDimensions } from "@app/contexts";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";
import { selectContractorsGrid } from "@app/statefx/selectors/uiContractorEditing";
import { setContractorsEditingProp } from "@app/statefx/xThunks/uiContractorsEditing";

import { isAPossibleCIF } from "./helpers";

import type { DataGridRef } from "devextreme-react/data-grid";
export const ContractorsGrid = () => {
  const dispatch = useFxDispatch();
  const ctGridRef = React.useRef<DataGridRef>(null);
  const { t } = useTranslation();
  const { jgDrawerHeight } = useWindowDimensions();

  const dataSource = useFxSelector(selectContractorsGrid);
  const uiContractorsEditing = useFxSelector(
    schema.uiContractorsEditing.select
  );
  const cSelected = useMemo(
    () => uiContractorsEditing.mainCffId,
    [uiContractorsEditing]
  );
  const rowPrepared = (e: any) => {
    void 0;
  };
  const setSearchingCif = (cif: string) => {
    dispatch(setContractorsEditingProp({ searchCIF: cif }));
  };
  const searchChanged = (e: any) => {
    if (isAPossibleCIF(e)) {
      // will dispatch a search for the CFFId in the CIF form
      setSearchingCif(e);
    }
  };

  const selectionChanged = (e: any) => {
    if (e.selectedRowKeys.length > 0) {
      dispatch(setContractorsEditingProp({ mainCffId: e.selectedRowKeys[0] }));
    }
    if (e.selectedRowsData?.[0]?.TaxCode) {
      if (isAPossibleCIF(e.selectedRowsData[0].TaxCode)) {
        setSearchingCif(e.selectedRowsData[0].TaxCode);
      }
    }
  };

  return (
    <DataGrid
      ref={ctGridRef}
      showBorders={true}
      style={{ overflow: "auto", maxWidth: "1344px" }}
      className="dx-swatch-catetory-chooser-compact"
      height={jgDrawerHeight}
      width={"100%"}
      keyExpr={"CffId"}
      dataSource={dataSource}
      filterSyncEnabled={true}
      allowColumnReordering={true}
      allowColumnResizing={true}
      showRowLines={true}
      showColumnLines={false}
      selectedRowKeys={[cSelected]}
      onRowPrepared={rowPrepared}
      onSelectionChanged={selectionChanged}
    >
      <Paging enabled={false} />
      <Selection mode="single" />
      <ColumnChooser enabled={true} />
      <SearchPanel visible={true} onTextChange={searchChanged} />
      <HeaderFilter visible={true} />
      <Grouping
        autoExpandAll={true}
        contextMenuEnabled={false}
        expandMode="rowClick"
      />
      <GroupPanel visible={false} allowColumnDragging={false} />
      <Toolbar>
        <Item location="center" name="searchPanel" />
        <Item
          location="after"
          widget="dxButton"
          options={{
            icon: "plus",
            text: t("new"),
            stylingMode: "contained",
            type: "default",
            onClick: () => {
              dispatch(setContractorsEditingProp({ mainCffId: -1 }));
            },
          }}
        />

        <Item
          location="after"
          widget="dxButton"
          options={{
            icon: "clear",
            text: t("clear"),
            stylingMode: "outlined",
            type: "default",
            onClick: () => {
              dispatch(setContractorsEditingProp({ mainCffId: -1 }));
              dispatch(setContractorsEditingProp({ searchCIF: "" }));
            },
          }}
        />

        <Item
          location="after"
          widget="dxButton"
          visible={false}
          options={{
            icon: "trash",
            stylingMode: "outlined",
            type: "danger",
            onClick: () => {
              //
            },
          }}
        />
      </Toolbar>
      <Column dataField="CffId" visible={false} allowSearch={false} />
      <Column
        dataField="TaxCode"
        caption={t("TaxCode")}
        visible={false}
        allowSearch={true}
      />
      <Column
        dataField="ClientName"
        caption={t("Name")}
        allowSearch={true}
        sortOrder="asc"
      />
    </DataGrid>
  );
};
